import type { Block } from '@pageboss/shared';
import { InspectorSection } from './inspector-section';
import { InspectorField } from './inspector-field';
import { Toggle } from './toggle';

type ContactBlock = Extract<Block, { type: 'contact' }>;
type ContactProps = ContactBlock['props'];

// Schema accepts placeholderUrlSchema and z.string().email() | ''
// for email — empty strings are valid placeholders. We do soft client-
// side validation here so the owner sees a helpful hint, but autosave
// goes through regardless (matches the rest of the inspector's
// soft-validation approach).
function emailError(value: string): string | undefined {
  if (!value) return undefined;
  // Match the same shape Zod's .email() uses internally — RFC simplified.
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
    return 'Looks off — make sure it\'s a real email address.';
  }
  return undefined;
}

function urlError(value: string): string | undefined {
  if (!value) return undefined;
  try {
    new URL(value);
    return undefined;
  } catch {
    return 'Looks off — paste the full URL including https://';
  }
}

interface ContactInspectorProps {
  block: ContactBlock;
  onChange: (next: ContactProps) => void;
}

export function ContactInspector({ block, onChange }: ContactInspectorProps) {
  const props = block.props;
  const patch = (next: Partial<ContactProps>) => onChange({ ...props, ...next });
  const emailErr = emailError(props.email ?? '');
  const mapUrlErr = urlError(props.mapEmbedUrl ?? '');

  return (
    <>
      <InspectorSection>
        <div className="flex flex-col gap-4">
          <InspectorField
            label="Title"
            value={props.title ?? ''}
            placeholder="Get in touch"
            onChange={(v) => patch({ title: v })}
          />
          <InspectorField
            label="Description"
            value={props.description ?? ''}
            multiline
            rows={2}
            placeholder="We usually reply within an hour."
            onChange={(v) => patch({ description: v })}
          />
        </div>
      </InspectorSection>

      <InspectorSection label="Details">
        <div className="flex flex-col gap-4">
          <InspectorField
            label="Email"
            type="email"
            value={props.email ?? ''}
            placeholder="hello@yourbusiness.com"
            {...(emailErr ? { errorText: emailErr } : {})}
            onChange={(v) => patch({ email: v })}
          />
          <InspectorField
            label="Phone"
            type="tel"
            value={props.phone ?? ''}
            placeholder="(619) 555-0100"
            onChange={(v) => patch({ phone: v })}
          />
          <InspectorField
            label="Address"
            value={props.address ?? ''}
            multiline
            rows={2}
            placeholder="1234 Main St, San Diego, CA 92101"
            onChange={(v) => patch({ address: v })}
          />
        </div>
      </InspectorSection>

      <InspectorSection label="Form">
        <div className="flex flex-col gap-4">
          <Toggle
            label="Show contact form"
            description="Adds a name + email + message form below the details"
            checked={props.formEnabled}
            onChange={(checked) => patch({ formEnabled: checked })}
          />
        </div>
      </InspectorSection>

      <InspectorSection label="Map (optional)">
        <InspectorField
          label="Google Maps embed URL"
          type="url"
          value={props.mapEmbedUrl ?? ''}
          placeholder="https://www.google.com/maps/embed?pb=..."
          helperText="Paste the iframe src from Google Maps → Share → Embed"
          {...(mapUrlErr ? { errorText: mapUrlErr } : {})}
          onChange={(v) => patch({ mapEmbedUrl: v })}
        />
      </InspectorSection>
    </>
  );
}
